import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactDOM from 'react-dom';
import {FormattedMessage, injectIntl} from 'react-intl';
import Loadable from 'react-loadable';

import {Badge, Icon} from '@stubhub/bamboo';
import {BubblesSkeleton} from '@stubhub/react-skeletons';

import IconWithTooltip from '../../../../components/react-icon-with-tooltip';
import {Section, Row, isRowValid} from '../../../../components/react-section-row-seat';
import {getTicketListIcons, VIP_Hospitality} from '../../../../utils/listingAttributeIconHelper';
import {hasPartnerTicketAttribute} from '../../../../utils/listingAttributesHelper';

import messages from './messages';
import {getDeliveryHighlight} from './utils';

import './index.scss';

/* Dynamic import */
// Might Use Later
// Const SeatViewImageUnavailable = Loadable({
//   Loader: () =>
//   /* istanbul ignore next */ import(
//     /* webpackChunkName: "swi-SeatmapUnavailable" */ "../react-ticket-list-no-seatview"
//   ),
//   Loading: () => /* istanbul ignore next */ null
// });

const ValueScore = /* istanbul ignore next */ Loadable({
  loader: () =>
    /* istanbul ignore next */ import(
      /* WebpackChunkName: "swi-ValueScore" */ '../../../../components/react-value-score'
    ),
  loading: () => /* istanbul ignore next */ null,
});

const Price = Loadable({
  loader: () =>
    /* istanbul ignore next */ import(/* WebpackChunkName: "swi-Price" */ '../../../../components/react-price'),
  loading: () => /* istanbul ignore next */ null,
});

const TicketListIconList = ({
  eventLoading = false,
  hasAttributes = false,
  listingAttributeList = {},
  showBrokerInfo = false,
  isPartnerTicket = false,
  isTrader = false,
  sellerOwnInd = 0,
}) => {
  const iconList = getTicketListIcons(listingAttributeList);

  showBrokerInfo &&
    isPartnerTicket &&
    iconList.push({
      icon: <Icon name="PrimaryPartner" />,
      label: <FormattedMessage id="ticket.listIcons.partner" defaultMessage="Partner tickets" />,
    });

  isTrader &&
    iconList.push({
      id: 'trader',
      icon: <Icon name="SellerTrader" />,
      label: <FormattedMessage id="ticket.listIcons.trader" defaultMessage="Seller is a trader" />,
    });

  // Adding keys and being more semantic
  const iconListAsListElements = iconList.map((icon, index) => (
    <li key={`TicketListIcon_${index}`}>
      <Badge appearance="neutral-lighten" className="ticket-list-icon__badge">
        <IconWithTooltip
          tooltipId={`ticket-list-icon-tooltip-${icon.id}`}
          data-testid={`ticket-list-icon-tooltip-${icon.id}`}
          tooltipText={icon.label}
          className="ticket-list-icon__tooltip"
        >
          {icon.icon}
        </IconWithTooltip>
      </Badge>
    </li>
  ));

  if (sellerOwnInd) {
    iconListAsListElements.push(
      <li key="TicketListIcon_MyListing">
        <span className="RoyalTicketListPanel__mylistingstring">
          <FormattedMessage id="RoyalTicketListPanel.myListing" defaultMessage="My Listing" />
        </span>
      </li>
    );
  }

  const showLoadingSkeleton = eventLoading && (iconList.length > 0 || hasAttributes);
  if (showLoadingSkeleton) {
    const loadingSkeleton = (
      <li key="TicketListIcon--loading" className="TicketListIcon--loading">
        <BubblesSkeleton />
      </li>
    );

    return <ul className="RoyalTicketListPanel__iconList">{loadingSkeleton}</ul>;
  }

  return iconListAsListElements.length ? (
    <ul className="RoyalTicketListPanel__iconList">{iconListAsListElements}</ul>
  ) : null;
};

TicketListIconList.propTypes = {
  /**
   * If event is loading
   */
  eventLoading: PropTypes.bool,

  /**
   * If listing has attributes
   */
  hasAttributes: PropTypes.bool,

  /**
   * Listing attributes list
   */
  listingAttributeList: PropTypes.object,

  /**
   * If broker information can be shown
   */
  showBrokerInfo: PropTypes.bool,

  /**
   * If is partner
   */
  isPartnerTicket: PropTypes.bool,

  /**
   * If is trader
   */
  isTrader: PropTypes.bool,

  /**
   * If is seller Own
   */
  sellerOwnInd: PropTypes.number,
};

export const TicketList__SecondaryInfo = ({
  row = '',
  splitVector = [],
  isListingGA = false,
  isInstantDelivery = false,
  isFastDelivery = false,
  isListingVIP = false,
  TicketListIconList = null,
}) => {
  let ticketCount = splitVector?.[0];

  if (ticketCount && splitVector[0] !== splitVector[splitVector.length - 1]) {
    ticketCount = `${splitVector[0]} - ${splitVector[splitVector.length - 1]}`;
  }

  const rowIsValid = isRowValid(row);

  const filteredInfoArray = [
    rowIsValid && !isListingGA && <Row row={row} key={row} />,
    ticketCount && (
      <React.Fragment key={`${row}-count`}>
        <FormattedMessage
          id="event-royal.react-ticket-list-panel.ticketqty"
          values={{ticketCount}}
          defaultMessage="{ticketCount} {ticketCount, plural, one {ticket} other {tickets}}"
        />
      </React.Fragment>
    ),
    <div key={`${row}-iv`} className="RoyalTicketListPanel__InstantDeliveryStringAndVipString">
      {isInstantDelivery && (
        <IconWithTooltip
          tooltipId={`ticket-list-icon-tooltip-instant-delivery-${row}`}
          data-testid={`ticket-list-icon-tooltip-instant-delivery-${row}`}
          tooltipText={<FormattedMessage {...messages.instantDeliveryTooltip} />}
          className="ticket-list-icon__tooltip"
        >
          <Badge appearance="success-lighten" size="lg">
            <span className="RoyalTicketListPanel__InstantDeliveryString">
              <Icon className="RoyalTicketListPanel__icon" name="Download" size="sm" fill="#008036" />
              <FormattedMessage id="RoyalTicketListPanel.instantdownload" defaultMessage="Instant Download" />
            </span>
          </Badge>
        </IconWithTooltip>
      )}
      {isFastDelivery && (
        <IconWithTooltip
          tooltipId={`ticket-list-icon-tooltip-fast-delivery-${row}`}
          data-testid={`ticket-list-icon-tooltip-fast-delivery-${row}`}
          tooltipText={<FormattedMessage {...messages.fastDeliveryTooltip} />}
          className="ticket-list-icon__tooltip"
        >
          <Badge appearance="info-lighten" size="lg">
            <span className="RoyalTicketListPanel__InstantDeliveryString">
              <Icon
                className="RoyalTicketListPanel__icon RoyalTicketListPanel__icon__fast"
                name="Fast"
                size="sm"
                fill="#0e8d93"
              />
              <FormattedMessage id="RoyalTicketListPanel.fastdelivery" defaultMessage="Fast Delivery" />
            </span>
          </Badge>
        </IconWithTooltip>
      )}
      {TicketListIconList}
      {isListingVIP && (
        <Badge appearance="neutral-lighten" className="RoyalTicketListPanel__VIPBadge">
          <span>
            <FormattedMessage id="RoyalTicketListPanel.viphospitality" defaultMessage="VIP Hospitality" />
          </span>
        </Badge>
      )}
    </div>,
  ].filter(Boolean);

  return <div className="RoyalTicketListPanel__SecondaryInfo">{filteredInfoArray}</div>;
};

TicketList__SecondaryInfo.propTypes = {
  row: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  splitVector: PropTypes.array,
  isListingGA: PropTypes.bool,
  isInstantDelivery: PropTypes.bool,
  isFastDelivery: PropTypes.bool,
  isListingVIP: PropTypes.bool,
  TicketListIconList: PropTypes.node,
};

class RoyalTicketListPanel extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showImage: false,
      topPosition: 0,
      rightPosition: 0,
      errorImage: false,
    };

    this.panelRef;
  }

  showVFSImage = () => {
    const {errorImage} = this.state;
    if (!errorImage) {
      const elementLocation = this.panelRef.getBoundingClientRect();
      const {top = 0, right = 0} = elementLocation;
      this.setState({
        showImage: true,
        topPosition: top,
        rightPosition: right,
      });
    }
  };

  hideVFSImage = () => {
    this.setState({showImage: false});
  };

  errorVFSImage = () => {
    this.setState({errorImage: true, showImage: false});
  };

  getSidFromHoveredListing = () => {
    const {handleHoveredListingSectionId, sectionId, zoneId} = this.props;
    handleHoveredListingSectionId && handleHoveredListingSectionId(sectionId, zoneId);
  };

  resetHoveredSid = () => {
    const {handleHoveredListingSectionId} = this.props;
    handleHoveredListingSectionId && handleHoveredListingSectionId();
  };

  hoverOn = () => {
    this.showVFSImage();
    this.getSidFromHoveredListing();
  };

  hoverOff = () => {
    this.hideVFSImage();
    this.resetHoveredSid();
  };

  render() {
    const {
      listingId,
      isListingGA,
      isEventGA,
      index = '',
      srs,
      image,
      price,
      viewFromSection,
      handleSelectTicket,
      valuePercentage,
      showTicketRecoInd,
      isInstantDelivery = false,
      isFastDelivery = false,
      ticketRank,
      isTrader = false,
      listingAttributeList = [],
      listingAttributeListWithCategories = {},
      showBrokerInfo = false,
      ticketListScrolling = false,
      splitVector = [],
      sellerOwnInd = 0,
      eventLoading = false,
      intl,
      zoneColor,
      deliveryTypeList = [],
      expectedInhandDate,
    } = this.props;

    const ticketListPanelIndex = (index !== '' && `RoyalTicketListPanel__${index}`) || '';
    const isViewFromSectionAvailable = viewFromSection && Boolean(image);
    const isPartnerTicket = hasPartnerTicketAttribute(listingAttributeList);
    const {section = '', row = '', seats = ''} = srs;

    const isListingVIP = VIP_Hospitality.has(...listingAttributeList);

    const {showImage, topPosition, rightPosition} = this.state;

    const shouldShowImage = isViewFromSectionAvailable && showImage && !ticketListScrolling;
    const zoneBorderColor = zoneColor ? {borderLeftColor: `${zoneColor}`} : {};
    const deliveryHighlight = getDeliveryHighlight(deliveryTypeList, expectedInhandDate);

    return (
      <li
        className={classNames('RoyalTicketListPanel', ticketListPanelIndex, {
          'RoyalTicketListPanel--withColor': !!zoneColor,
        })}
        onClick={(e) => {
          handleSelectTicket && handleSelectTicket(e, listingId, ticketRank);
          this.hideVFSImage();
        }}
        onMouseOver={this.hoverOn}
        onMouseLeave={this.hoverOff}
        ref={(el) => (this.panelRef = el)}
        style={zoneBorderColor}
        data-delivery-type-list={deliveryTypeList.join(',')}
        data-expected-in-hand-date={expectedInhandDate}
        {...(deliveryHighlight && {'data-delivery-highlight': deliveryHighlight})}
      >
        <div className="RoyalTicketListPanel__column">
          <Section section={section} isEventGA={isEventGA} className="RoyalTicketListPanel__SectionName" />
          <TicketList__SecondaryInfo
            isInstantDelivery={isInstantDelivery}
            isFastDelivery={isFastDelivery}
            splitVector={splitVector}
            isListingVIP={isListingVIP}
            isListingGA={isListingGA}
            row={row}
            seats={seats}
            TicketListIconList={
              <TicketListIconList
                eventLoading={eventLoading}
                hasAttributes={listingAttributeList.length > 0}
                listingAttributeList={listingAttributeListWithCategories}
                showBrokerInfo={showBrokerInfo}
                isPartnerTicket={isPartnerTicket}
                isTrader={isTrader}
                sellerOwnInd={sellerOwnInd}
              />
            }
          />
        </div>

        <div className="RoyalTicketListPanel__column">
          <Price className="RoyalTicketListPanel__price" currency={price.currency} amount={price.amount} />
          {showTicketRecoInd && valuePercentage && <ValueScore valuePercentage={valuePercentage} />}
        </div>
        {shouldShowImage && (
          <ImagePortal
            component={
              <img
                className="RoyalTicketListPanel__image"
                style={{top: topPosition, left: rightPosition}}
                src={image}
                alt={intl.formatMessage(messages.VFS, {section})}
                onClick={this.hideVFSImage}
                onError={this.errorVFSImage}
              />
            }
          />
        )}
      </li>
    );
  }
}

RoyalTicketListPanel.propTypes = {
  /**
   * Section,row and seats
   */
  srs: PropTypes.object,

  /**
   * Image url
   */
  image: PropTypes.string,

  /**
   * Price object with amount and currency
   */
  price: PropTypes.object,

  /**
   *
   */
  valuePercentage: PropTypes.number,

  /**
   * If is instanst delivery
   */
  isInstantDelivery: PropTypes.bool,

  /**
   * If is fast delivery
   */
  isFastDelivery: PropTypes.bool,

  /**
   * Section ID
   */
  sectionId: PropTypes.number,

  /**
   * Method to hover section ID in map
   */
  handleHoveredListingSectionId: PropTypes.func,

  /**
   * Listing ID
   */
  listingId: PropTypes.number,

  /**
   * If listing is general admission
   */
  isListingGA: PropTypes.bool,

  /**
   * If listing is general admission
   */
  isEventGA: PropTypes.bool,

  /**
   * Listing index
   */
  index: PropTypes.number,

  /**
   * If view from section is available
   */
  viewFromSection: PropTypes.bool,

  /**
   * Method to handle selected ticket
   */
  handleSelectTicket: PropTypes.func,

  /**
   * If ticket reco is shown
   */
  showTicketRecoInd: PropTypes.bool,

  /**
   * Ticket rank value
   */
  ticketRank: PropTypes.number,

  /**
   * If is trader
   */
  isTrader: PropTypes.bool,

  /**
   * Attributes list with categories
   */
  listingAttributeList: PropTypes.array,

  /**
   * Attributes list with categories
   */
  listingAttributeListWithCategories: PropTypes.object,

  /**
   * If broker information can be shown
   */
  showBrokerInfo: PropTypes.bool,

  /**
   * If is scrolling
   */
  ticketListScrolling: PropTypes.bool,
  splitVector: PropTypes.array,

  /**
   * If is seller Own
   */
  sellerOwnInd: PropTypes.number,

  /**
   * If event is loading
   */
  eventLoading: PropTypes.bool,

  /**
   * Intl for translation
   */
  intl: PropTypes.object,

  /**
   * Color hex code for zone
   */
  zoneColor: PropTypes.string,

  /**
   * Zone ID
   */
  zoneId: PropTypes.number,

  /** delivery type id list */
  deliveryTypeList: PropTypes.arrayOf(PropTypes.number),

  /** expected in-hand date */
  expectedInhandDate: PropTypes.string,
};

const ImagePortal = ({component}) => {
  if (__CLIENT__ && document) {
    const bodyContainer = document.querySelector('.EventRoyal__DesktopLayout__BodyContainer');

    return bodyContainer && ReactDOM.createPortal(component, bodyContainer);
  }

  return null;
};

ImagePortal.propTypes = {
  /**
   * Component passed as children in props
   */
  component: PropTypes.node,
};

export default injectIntl(RoyalTicketListPanel);
